import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Button,
  Card,
  CardContent,
  Badge,
  toast 
} from '@etherisc/ui-kit';
import { productService } from '../../services/productService';
import { organizationService } from '../../services/organizationService';
import { schemaService } from '../../services/schemaService';
import { apiClient } from '../../services/api';
import type { Product, UpdateProductData, ProductConfig } from '../../types/product';
import { PRODUCT_STATUS_LABELS } from '../../types/product';
import { ProductConfigForm } from '../../components/product/ProductConfigForm';
import { ChevronLeftIcon, FileTextIcon, SaveIcon } from 'lucide-react';
import { ValidationErrorReport } from '../../components/common/ValidationErrorReport';
import { getErrorMessage } from '../../utils/errorHandling';
import { validateCoverageLevelLimits } from '../../utils/productValidation';


const getStatusColor = (status: string) => {
  switch (status) {
    case 'ACTIVE':
      return 'bg-green-100 text-green-800';
    case 'DRAFT':
      return 'bg-yellow-100 text-yellow-800';
    case 'PAUSED':
      return 'bg-orange-100 text-orange-800';
    case 'ARCHIVED':
      return 'bg-gray-100 text-gray-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getSchemaVersion = (productConfig: ProductConfig): string => {
  return productConfig?._version || 'v1.5.0'; // Fallback - will be replaced by dynamic version
};

/**
 * Normalize stakeholder structure to ensure required entries exist
 */
const normalizeStakeholderStructure = (config: ProductConfig, myubuntuOrg?: { id: string; name: string }): ProductConfig => {
  // Ensure stakeholdersAnrpDistribution exists
  if (!config.stakeholdersAnrpDistribution) {
    config.stakeholdersAnrpDistribution = {
      administration: {
        insurer: {
          organizationId: '00000000-0000-0000-0000-000000000001',
          name: 'Select Insurance Company',
          percentage: 0.8, // 80%
          role: 'Insurer'
        },
        myubuntu: {
          organizationId: myubuntuOrg?.id || '00000000-0000-0000-0000-000000000002',
          name: myubuntuOrg?.name || 'MyUbuntu Ltd',
          percentage: 0.15, // 15%
          role: 'MyUbuntu'
        }
      },
      underwriters: [{
        organizationId: '00000000-0000-0000-0000-000000000001',
        name: 'Select Underwriter',
        percentage: 0.05, // 5%
        isInsurer: true
      }],
      brokers: [],
      calculatingAgents: [],
      otherOrganisations: []
    };
    return config;
  }
  
  const stakeholders = config.stakeholdersAnrpDistribution;
  
  // Handle legacy dynamic administration structure
  if (stakeholders.administration && typeof stakeholders.administration === 'object') {
    // Check if it's the old dynamic structure (Record<string, StakeholderInfo>)
    const adminEntries = Object.entries(stakeholders.administration);
    const hasInsurerKey = 'insurer' in stakeholders.administration;
    const hasMyubuntuKey = 'myubuntu' in stakeholders.administration;
    
    if (!hasInsurerKey || !hasMyubuntuKey) {
      // Convert or create the proper structure
      const newAdministration = {
        insurer: hasInsurerKey 
          ? stakeholders.administration.insurer 
          : {
              organizationId: '00000000-0000-0000-0000-000000000001',
              name: 'Select Insurance Company',
              percentage: 0.8, // 80%
              role: 'Insurer' as const
            },
        myubuntu: hasMyubuntuKey 
          ? stakeholders.administration.myubuntu 
          : {
              organizationId: myubuntuOrg?.id || '00000000-0000-0000-0000-000000000002',
              name: myubuntuOrg?.name || 'MyUbuntu Ltd',
              percentage: 0.15, // 15%
              role: 'MyUbuntu' as const
            }
      };
      
      // If there are other entries in the legacy structure, try to preserve them
      for (const [key, value] of adminEntries) {
        if (key !== 'insurer' && key !== 'myubuntu' && value && typeof value === 'object') {
          // Move legacy entries to otherOrganisations
          if (!stakeholders.otherOrganisations) stakeholders.otherOrganisations = [];
          const legacyEntry = value as { organizationId?: string; name?: string; percentage?: number; role?: string };
          stakeholders.otherOrganisations.push({
            organizationId: legacyEntry.organizationId || '00000000-0000-0000-0000-000000000001',
            name: legacyEntry.name || 'Legacy Entry',
            percentage: legacyEntry.percentage || 0,
            role: legacyEntry.role || 'Other'
          });
        }
      }
      
      stakeholders.administration = newAdministration;
    }
  } else {
    // Create administration if it doesn't exist or is invalid
    stakeholders.administration = {
      insurer: {
        organizationId: '00000000-0000-0000-0000-000000000001',
        name: 'Select Insurance Company',
        percentage: 0.8, // 80%
        role: 'Insurer'
      },
      myubuntu: {
        organizationId: myubuntuOrg?.id || '00000000-0000-0000-0000-000000000002',
        name: myubuntuOrg?.name || 'MyUbuntu Ltd',
        percentage: 0.15, // 15%
        role: 'MyUbuntu'
      }
    };
  }
  
  // Ensure underwriters array exists and has at least one entry
  if (!stakeholders.underwriters || stakeholders.underwriters.length === 0) {
    stakeholders.underwriters = [{
      organizationId: '00000000-0000-0000-0000-000000000001',
      name: 'Select Underwriter',
      percentage: 0.05, // 5%
      isInsurer: true
    }];
  }
  
  // Ensure other arrays exist
  if (!stakeholders.brokers) stakeholders.brokers = [];
  if (!stakeholders.calculatingAgents) stakeholders.calculatingAgents = [];
  if (!stakeholders.otherOrganisations) stakeholders.otherOrganisations = [];
  
  // Ensure currency is in coverageConfig
  if (!config.coverageConfig.currency) {
    // Check if currency exists in stakeholders (legacy) and move it
    const stakeholdersWithCurrency = stakeholders as typeof stakeholders & { currency?: string };
    if (stakeholdersWithCurrency.currency) {
      config.coverageConfig.currency = stakeholdersWithCurrency.currency;
      delete stakeholdersWithCurrency.currency;
    } else {
      config.coverageConfig.currency = 'USD';
    }
  }
  
  return config;
};

export function EditProduct() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [loadingProduct, setLoadingProduct] = useState(true);
  
  const [product, setProduct] = useState<Product | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    version: '',
    tags: '',
    status: 'ACTIVE' as 'ACTIVE' | 'DRAFT' | 'PAUSED' | 'ARCHIVED',
    quotationScript: '',
    claimsScript: '',
  });

  // Product config state - version will be set dynamically
  const [productConfig, setProductConfig] = useState<ProductConfig>({
    _version: '1.5.0', // Will be updated with current version from schema registry
    coverageConfig: {
      activitiesOrProducts: ['maize', 'sorghum', 'groundnuts'],
      perils: [], // Start empty - will be populated when product loads
      areaOptions: ['Country-wide'],
      coveragePeriodLimits: {
        type: 'duration_days',
        minDurationDays: 90,
        maxDurationDays: 365,
      },
      sumInsuredLimits: {
        min: 500,
        max: 100000,
      },
      coverageLevelGlobalLimits: {},
      currency: 'USD' // Primary currency field
    },
    loadingConfig: {
      taxRate: 0.15,
      subsidyRate: 0.0,
      loadingFactors: [],
      perilWeighting: {
        enforcement: 'default',
        weights: {}
      },
              coverageLevelLimits: {
          enforcement: 'default',
          limits: {}
        },
    },
    stakeholdersAnrpDistribution: {
      administration: {
        insurer: {
          organizationId: '00000000-0000-0000-0000-000000000001',
          name: 'Select Insurance Company',
          percentage: 0.05,
          role: 'Insurer',
        },
        myubuntu: {
          organizationId: '00000000-0000-0000-0000-000000000002',
          name: 'MyUbuntu Ltd',
          percentage: 0.1,
          role: 'MyUbuntu',
        },
      },
      underwriters: [{
        organizationId: '00000000-0000-0000-0000-000000000001',
        name: 'Select Underwriter',
        percentage: 0.05, // 5%
        isInsurer: true,
      }],
      brokers: [],
      calculatingAgents: [],
      otherOrganisations: [],
    },
    pricingFactors: {
      minimumPremium: 75,
      administrativeFee: 35,
      underwritingLoading: 0.03,
      agentCommissionRate: 0.12,
      brokerCommissionRate: 0.08,
      subsidyRate: 0,
    },
  });

  const [validationErrors, setValidationErrors] = useState<Array<{
    path: string;
    message: string;
    value: unknown;
  }>>([]);

  const [cronExpression, setCronExpression] = useState<string>('');
  const [automationScheduleId, setAutomationScheduleId] = useState<string | null>(null);

  // Load product data on mount
  useEffect(() => {
    const loadProduct = async () => {
      if (!id) {
        toast.error('Product ID is required');
        navigate('/admin/products');
        return;
      }

      try {
        setLoadingProduct(true);
        
        // Load product, MyUbuntu organization, and current schema version in parallel
        const [productResponse, myubuntuOrg, currentVersion] = await Promise.all([
          productService.getProductById(id),
          organizationService.getMyUbuntuOrganization().catch(() => null), // Don't fail if MyUbuntu not found
          schemaService.getCurrentProductConfigVersion().catch(() => '1.5.0') // Fallback version
        ]);
        
        const productData = productResponse.product;
        setProduct(productData);
        
        // Set form data
        setFormData({
          name: productData.name,
          code: productData.code,
          description: productData.description || '',
          version: productData.version || '1.0.0',
          tags: productData.tags ? productData.tags.join(', ') : '',
          status: productData.status,
          quotationScript: productData.quotationScript || '',
          claimsScript: productData.claimsScript || '',
        });

        // Set product config - handle wrapped structure from backend
        if (productData.productConfig) {
          const config = productData.productConfig as unknown;
          
          let normalizedConfig: ProductConfig;
          
          // Type guard for wrapped format
          if (config && typeof config === 'object' && 'data' in config && 'schemaId' in config && 'schemaVersion' in config) {
            // Wrapped format: { data: ProductConfig, schemaId: string, schemaVersion: string }
            const wrappedConfig = config as { data: ProductConfig; schemaId: string; schemaVersion: string };
            normalizedConfig = { ...wrappedConfig.data, _version: currentVersion }; // Use current version from registry
          } else {
            // Direct format (fallback)
            normalizedConfig = { ...(productData.productConfig as ProductConfig), _version: currentVersion };
          }
          
          // Ensure required stakeholder structure exists
          normalizedConfig = normalizeStakeholderStructure(normalizedConfig, myubuntuOrg || undefined);
          
          setProductConfig(normalizedConfig);
        }

        // Load automation schedule for this product
        try {
          const scheduleRes = await apiClient.getAutomationSchedules('product', id);
          const schedules = scheduleRes?.schedules || [];
          const assessSchedule = schedules.find(s => s.taskType === 'claims_assessment');
          if (assessSchedule) {
            setCronExpression(assessSchedule.cronExpression);
            setAutomationScheduleId(assessSchedule.id);
          }
        } catch {
          // Non-critical: schedule may not exist yet
        }
      } catch (error) {
        console.error('Error loading product:', error);
        toast.error('Failed to load product');
        navigate('/admin/products');
      } finally {
        setLoadingProduct(false);
      }
    };

    loadProduct();
  }, [id, navigate]);

  const handleProductConfigChange = (newConfig: ProductConfig) => {
    setProductConfig(newConfig);
    setValidationErrors([]);
  };

  const handleCronExpressionChange = (cron: string) => {
    setCronExpression(cron);
  };

  const validateForm = (): boolean => {
    const errors: Array<{ path: string; message: string; value: unknown }> = [];

    // Validate basic form data
    if (!formData.name.trim()) {
      errors.push({
        path: 'name',
        message: 'Product name is required',
        value: formData.name
      });
    }

    if (!formData.code.trim()) {
      errors.push({
        path: 'code',
        message: 'Product code is required',
        value: formData.code
      });
    }

    // Validate product config
    const stakeholders = productConfig.stakeholdersAnrpDistribution;
    const totalPercentage = 
      stakeholders.administration.insurer.percentage +
      stakeholders.administration.myubuntu.percentage +
      stakeholders.underwriters.reduce((sum, u) => sum + u.percentage, 0) +
      (stakeholders.brokers?.reduce((sum, b) => sum + b.percentage, 0) || 0) +
      (stakeholders.calculatingAgents?.reduce((sum, c) => sum + c.percentage, 0) || 0) +
      (stakeholders.otherOrganisations?.reduce((sum, o) => sum + o.percentage, 0) || 0);

    if (Math.abs(totalPercentage - 1.0) > 0.001) {
      errors.push({
        path: 'stakeholdersAnrpDistribution',
        message: `Total ANRP percentage must equal 100%. Current: ${(totalPercentage * 100).toFixed(1)}%`,
        value: totalPercentage
      });
    }

    // Validate coverage level limits (#125, #126)
    errors.push(...validateCoverageLevelLimits(productConfig));

    setValidationErrors(errors);
    
    if (errors.length > 0) {
      console.group('Product Edit Validation Errors');
      console.table(errors.map(err => ({
        Field: err.path,
        Message: err.message,
        Value: err.value
      })));
      console.groupEnd();
    }
    
    return errors.length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Please fix validation errors before submitting');
      return;
    }

    if (!product) {
      toast.error('Product data not loaded');
      return;
    }

    setLoading(true);
    try {
      const updateData: UpdateProductData = {
        name: formData.name,
        code: formData.code,
        description: formData.description,
        version: formData.version,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(Boolean),
        status: formData.status,
        quotationScript: formData.quotationScript || undefined,
        claimsScript: formData.claimsScript || undefined,
        productConfig: (() => {
          // Remove _version from productConfig before sending to backend (it's only for frontend validation)
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { _version, ...productConfigForBackend } = productConfig;
          return productConfigForBackend;
        })()
      };

      await productService.updateProduct(product.id, updateData);

      // Save or remove the automation schedule in sync with the product config
      const assessAutomated = productConfig.automationConfig?.assess?.mode === 'automated';
      if (assessAutomated && cronExpression) {
        try {
          await apiClient.upsertAutomationSchedule({
            resourceType: 'product',
            resourceId: product.id,
            taskType: 'claims_assessment',
            cronExpression,
            enabled: true,
          });
        } catch (scheduleErr) {
          console.error('Failed to save automation schedule:', scheduleErr);
          toast.error('Product saved but automation schedule failed to save');
        }
      } else if (!assessAutomated && automationScheduleId) {
        try {
          await apiClient.deleteAutomationSchedule(automationScheduleId);
          setAutomationScheduleId(null);
        } catch {
          // Non-critical: schedule will be skipped by the worker guard
        }
      }

      toast.success('Product updated successfully!');
      navigate(`/admin/products/${product.id}`);
    } catch (error) {
      console.group('🚨 Product Update Backend Error');
      console.error('Error updating product:', error);
      
      // Enhanced error logging for debugging
      if (error && typeof error === 'object' && 'data' in error) {
        const apiError = error as { data?: { error?: { message?: string; details?: { field?: string; value?: unknown; reason?: string; validationErrors?: unknown[] } } } };
        console.log('Full error object:', error);
        console.log('Error data:', apiError.data);
        
        const errorData = apiError.data;
        const backendError = errorData?.error;
        
        // Check for validation errors in the nested error structure first
        if (backendError?.details?.validationErrors) {
          const backendErrors = backendError.details.validationErrors.map((err: unknown) => {
            const errorObj = err as { field?: string; message?: string; schema?: string; data?: unknown };
            return {
              path: errorObj.field || 'unknown',
              message: errorObj.message || 'Validation error',
              schema: errorObj.schema,
              data: errorObj.data,
              value: errorObj.data
            };
          });
          
          console.log('🔍 Backend validation errors:', backendErrors);
          console.log('🔍 Raw validation errors from backend:', JSON.stringify(backendError.details.validationErrors, null, 2));
          
          // Combine with existing frontend validation errors
          setValidationErrors(prev => [...prev, ...backendErrors]);
          
          toast.error('Validation Failed', { 
            description: `Found ${backendErrors.length} validation errors. Please check the form below.` 
          });
        }
        // Check for AppError (e.g., duplicate product code) if no validation errors
        else if (backendError?.message) {
          const errorMessage = getErrorMessage(backendError.message);
          
          // If it's a field-specific error, add it to validation errors
          if (backendError.details?.field) {
            const fieldError = {
              path: backendError.details.field,
              message: errorMessage,
              value: backendError.details.value
            };
            setValidationErrors(prev => [...prev, fieldError]);
            toast.error('Validation Error', { description: errorMessage });
          } else {
            toast.error(errorMessage);
          }
          console.log('🔍 Backend error:', backendError);
        } else {
          console.log('❌ No validation errors found in expected structure');
          console.log('Available error data keys:', errorData ? Object.keys(errorData) : 'No data');
          console.log('Backend error:', backendError);
          toast.error('Failed to update product. Please try again.');
        }
      } else {
        console.log('❌ Error does not have expected structure');
        toast.error('Failed to update product. Please try again.');
      }
      console.groupEnd();
    } finally {
      setLoading(false);
    }
  };

  if (loadingProduct) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading product...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="text-center py-16">
          <p className="text-red-600 text-lg">Product not found</p>
          <Button onClick={() => navigate('/admin/products')} className="mt-4">
            Back to Products
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <div className="flex items-center gap-4 mb-8">
        <Button 
          variant="outline" 
          onClick={() => navigate('/admin/products')}
          className="flex items-center gap-2"
        >
          <ChevronLeftIcon size={16} />
          Back to Products
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <FileTextIcon size={32} />
            {product?.name || 'Edit Product'}
          </h1>
          {product && (
            <div className="text-gray-600 mt-1 flex items-center gap-4 text-sm">
              <span>{product.code}</span>
              <span>•</span>
              <span>Version {product.version || '1.0.0'}</span>
              <span>•</span>
              <span>Created {new Date(product.createdAt).toLocaleDateString()}</span>
              <span>•</span>
              <span>Updated {new Date(product.updatedAt).toLocaleDateString()}</span>
              <Badge className={getStatusColor(product.status)}>
                {PRODUCT_STATUS_LABELS[product.status] || product.status}
              </Badge>
              {product.productConfig && (
                <Badge variant="secondary" className="text-xs">
                  {getSchemaVersion(product.productConfig)}
                </Badge>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="space-y-8">
        {/* Product Configuration (v1.4.0) */}
        <ProductConfigForm
          productConfig={productConfig}
          onChange={handleProductConfigChange}
          productName={formData.name}
          productCode={formData.code}
          productDescription={formData.description}
          productStatus={formData.status}
          productTags={formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag)}
          productVersion={formData.version}
          quotationScript={formData.quotationScript}
          claimsScript={formData.claimsScript}
          onProductFieldChange={(field, value) => {
            if (field === 'tags') {
              setFormData({ ...formData, [field]: Array.isArray(value) ? value.join(', ') : String(value) });
            } else {
              setFormData({ ...formData, [field]: value });
            }
          }}
          errors={validationErrors}
          cronExpression={cronExpression}
          onCronExpressionChange={handleCronExpressionChange}
        />

        {/* Validation Error Report */}
        {validationErrors.length > 0 && (
          <ValidationErrorReport 
            errors={validationErrors.map(err => ({
              ...err,
              field: err.path || 'unknown'
            }))}
            title="Please fix these issues before updating the product"
          />
        )}

        {/* Submit Actions */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-end gap-4">
              <Button 
                variant="outline" 
                onClick={() => navigate('/admin/products')}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSubmit}
                disabled={loading}
                className="flex items-center gap-2"
              >
                <SaveIcon size={16} />
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}